import { useQuery } from '@tanstack/react-query';
import { httpClient } from '@/utils/httpClient';
import dayjs from 'dayjs';
import type { NotificationLabels } from '@/hooks/useNotificationLabels';

const DEFAULT_NOTIFICATION_LABELS: NotificationLabels = {
  changeTypeLabel: 'Kit Changes',
  changeNounLabel: 'Kit',
  unknownKitFallback: 'Unknown Kit',
  summaryPhrase: 'kit changes',
  ieKitChangesLabel: 'IE Kit Changes',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AppNotification {
  id: string;
  type: 'thr_overdue' | 'expired' | 'expiring_soon' | 'boi_changes';
  label: string;
  title: string;
  body: string;
  date: string | null;
  actionLabel: string;
  actionRoute: string;
}

interface RawOverdueThrItem {
  id: string;
  service_member: string | null;
  dodid: string | null;
  asset: string | null;
  asset_identifier: string | null;
  due_date: string | null;
  days_overdue: number | null;
}

interface RawPhrItem {
  id: string;
  item: string | null;
  lin: string | null;
  serial_number: string | null;
  expiration_date: string | null;
}

interface RawBoiChange {
  id: string;
  kit_assignment_name: string | null;
  kit_name: string | null;
  change_type: 'assigned' | 'unassigned' | 'changed';
  updated_at: string | null;
  created_at: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmtDate = (d: string | null): string => {
  if (!d) return '—';
  return dayjs(d).format('M/D/YYYY');
};

const daysAgo = (d: string | null): number => {
  if (!d) return 0;
  return dayjs().diff(dayjs(d), 'day');
};

const toOverdueNotification = (item: RawOverdueThrItem): AppNotification => {
  const asset = item.asset ?? 'Unknown Item';
  const days = item.days_overdue ?? 0;
  const dueStr = fmtDate(item.due_date);
  const lin = item.asset_identifier ? ` / SN ${item.asset_identifier}` : '';
  return {
    id: `thr_overdue_${item.id}`,
    type: 'thr_overdue',
    label: 'THR Overdue',
    title: `${asset} — THR ${days} ${days === 1 ? 'day' : 'days'} overdue`,
    body: `Your Temporary Hand Receipt for ${asset}${lin} was due ${dueStr} and has not been turned in. This item is now ${days} days past due. Contact your property book officer immediately to avoid accountability action.`,
    date: item.due_date,
    actionLabel: 'View Transaction History',
    actionRoute: '/transactions',
  };
};

const toExpiredNotification = (item: RawPhrItem): AppNotification => {
  const itemName = item.item ?? 'Unknown Item';
  const lin = item.lin
    ? ` / LIN ${item.lin}`
    : item.serial_number
      ? ` / SN ${item.serial_number}`
      : '';
  const days = daysAgo(item.expiration_date);
  const expStr = fmtDate(item.expiration_date);
  return {
    id: `expired_${item.id}`,
    type: 'expired',
    label: 'Expired',
    title: `${itemName} — EXPIRED`,
    body: `Item ${itemName}${lin} expired ${expStr} (${days} ${days === 1 ? 'day' : 'days'} ago). This item must be turned in or replaced. Contact your property book officer for guidance.`,
    date: item.expiration_date,
    actionLabel: 'View CIF Hand Receipt',
    actionRoute: '/transactions',
  };
};

const toExpiringSoonNotification = (item: RawPhrItem): AppNotification => {
  const itemName = item.item ?? 'Unknown Item';
  const lin = item.lin
    ? ` / LIN ${item.lin}`
    : item.serial_number
      ? ` / SN ${item.serial_number}`
      : '';
  const daysUntil = item.expiration_date
    ? dayjs(item.expiration_date).startOf('day').diff(dayjs().startOf('day'), 'day')
    : 0;
  const expStr = fmtDate(item.expiration_date);
  return {
    id: `expiring_soon_${item.id}`,
    type: 'expiring_soon',
    label: 'Expiring Soon',
    title: `${itemName} — Expiring in ${daysUntil} ${daysUntil === 1 ? 'day' : 'days'}`,
    body: `Item ${itemName}${lin} expires ${expStr}. Ensure this item is renewed or exchanged before the expiration date.`,
    date: item.expiration_date,
    actionLabel: 'View CIF Hand Receipt',
    actionRoute: '/transactions',
  };
};

const toBoiChangeNotification = (
  item: RawBoiChange,
  labels: NotificationLabels,
): AppNotification => {
  const kitName = item.kit_name ?? item.kit_assignment_name ?? labels.unknownKitFallback;
  const changeDate = item.updated_at ?? item.created_at;
  const dateStr = fmtDate(changeDate);
  const verb =
    item.change_type === 'assigned'
      ? 'assigned'
      : item.change_type === 'unassigned'
        ? 'unassigned'
        : 'updated';
  return {
    id: `boi_changes_${item.id}`,
    type: 'boi_changes',
    label: labels.changeTypeLabel,
    title: `${kitName} — ${labels.changeNounLabel} ${verb}`,
    body: `Your ${labels.changeNounLabel} assignment for ${kitName} was ${verb} on ${dateStr}. Review your current ${labels.changeNounLabel} equipment on the hand-receipt screen.`,
    date: changeDate,
    actionLabel: 'View Hand Receipt',
    actionRoute: '/transactions',
  };
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetch personal notifications for the current user.
 *
 * @param personEntityId - When provided, an explicit `person_id=eq.<id>` filter
 *   is added to every API query so that dual-role (CIF/HR/Comptroller/Finance)
 *   users who are also linked persons see only their own rows and not all rows.
 *   For service members the DB views already apply RLS, but passing the id is
 *   harmless and makes the scoping explicit everywhere (fail-closed design, #347).
 */
export const useMyNotifications = (options?: {
  personEntityId?: string | null;
  labels?: NotificationLabels;
}) => {
  const personEntityId = options?.personEntityId ?? null;
  const labels = options?.labels ?? DEFAULT_NOTIFICATION_LABELS;
  // Explicit person filter appended when known — empty string means no filter.
  const personFilter = personEntityId ? `&person_id=eq.${personEntityId}` : '';

  const today = dayjs().format('YYYY-MM-DD');
  const in30Days = dayjs().add(30, 'day').format('YYYY-MM-DD');
  // 30-day age-filter cutoff applied at the query boundary (#1024): rather
  // than fetching unbounded historical rows and relying solely on the
  // client-side filter below, the lower bound is sent as a query param so
  // the notifications list never requests data outside the default window.
  const cutoff30Date = dayjs().subtract(30, 'day').format('YYYY-MM-DD');

  const overdueQuery = useQuery({
    queryKey: ['my-notifications', 'overdue-thr', cutoff30Date, personEntityId],
    queryFn: () =>
      // overdue_thr_items has no person_id column; it is already scoped to the
      // current user via RLS, so no explicit person filter is applied here.
      // The view itself stays unbounded (also used by the dashboard alert
      // modal), so the 30-day floor is applied only on this request via
      // due_date=gte, matching the notifications list's own age window.
      httpClient.get<RawOverdueThrItem[]>(
        `/api/rest/data/v1/overdue_thr_items?due_date=gte.${cutoff30Date}`,
      ),
    staleTime: 2 * 60 * 1000,
  });

  const expiredPhrQuery = useQuery({
    queryKey: ['my-notifications', 'expired-phr', today, cutoff30Date, personEntityId],
    queryFn: () =>
      httpClient.get<RawPhrItem[]>(
        `/api/rest/data/v1/phr_items?expiration_date=not.is.null&expiration_date=lt.${today}&expiration_date=gte.${cutoff30Date}&select=id,item,lin,serial_number,expiration_date${personFilter}`,
      ),
    staleTime: 2 * 60 * 1000,
  });

  const expiringSoonPhrQuery = useQuery({
    queryKey: ['my-notifications', 'expiring-soon-phr', today, personEntityId],
    queryFn: () =>
      httpClient.get<RawPhrItem[]>(
        `/api/rest/data/v1/phr_items?expiration_date=not.is.null&expiration_date=gte.${today}&expiration_date=lte.${in30Days}&select=id,item,lin,serial_number,expiration_date${personFilter}`,
      ),
    staleTime: 2 * 60 * 1000,
  });

  const boiChangesQuery = useQuery({
    queryKey: ['my-notifications', 'boi-changes', personEntityId],
    queryFn: () =>
      httpClient.get<RawBoiChange[]>(
        `/api/rest/data/v1/my_boi_changes?select=id,kit_assignment_name,kit_name,change_type,updated_at,created_at${personFilter}`,
      ),
    staleTime: 2 * 60 * 1000,
  });

  const overdueNotifications = (overdueQuery.data ?? []).map(toOverdueNotification);
  const expiredNotifications = (expiredPhrQuery.data ?? []).map(toExpiredNotification);
  const expiringSoonNotifications = (expiringSoonPhrQuery.data ?? []).map(
    toExpiringSoonNotification,
  );
  const boiChangeNotifications = (boiChangesQuery.data ?? []).map((item) =>
    toBoiChangeNotification(item, labels),
  );

  // Strict rolling 30-day cutoff (#1024): an item is excluded once its date is
  // more than 30 full days before now, evaluated continuously against the
  // current instant rather than a calendar-month boundary. An item exactly
  // 30 days old is treated as outside the window (already >30 days elapsed
  // by the time this runs), while an item just under 30 days old remains
  // visible — see boundary-case regression tests in useMyNotifications.test.ts.
  const cutoff = dayjs().subtract(30, 'day');

  const notifications: AppNotification[] = [
    // THR overdue alerts drop off the default view after 30 days (#1024);
    // the underlying overdue_thr_items view is intentionally unbounded since
    // it's also used for the dashboard alert modal, so the cutoff is applied
    // here for the notifications list specifically.
    ...overdueNotifications.filter((n) => !n.date || dayjs(n.date).isAfter(cutoff)),
    // Expired items older than 30 days drop off the default view
    ...expiredNotifications.filter((n) => !n.date || dayjs(n.date).isAfter(cutoff)),
    // Expiring-soon alerts fall off after 30 days (item has since expired and moves to the expired list)
    ...expiringSoonNotifications.filter((n) => !n.date || dayjs(n.date).isAfter(cutoff)),
    // BOI changes are already pre-filtered to 30 days by the DB view
    ...boiChangeNotifications,
  ].sort((a, b) => {
    // Most recent first
    if (!a.date) return 1;
    if (!b.date) return -1;
    return b.date.localeCompare(a.date);
  });

  return {
    notifications,
    isLoading:
      overdueQuery.isLoading ||
      expiredPhrQuery.isLoading ||
      expiringSoonPhrQuery.isLoading ||
      boiChangesQuery.isLoading,
    isError:
      overdueQuery.isError ||
      expiredPhrQuery.isError ||
      expiringSoonPhrQuery.isError ||
      boiChangesQuery.isError,
  };
};
